import classNames from './TutorChat.module.scss';
import classes from 'classnames';
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DispatcherEvents, EventDispatcherContext } from '@/context/eventDispatcherContext';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import Chat, { ChatRef } from '@/components/ConversationWithTutor/Chat/Chat';
import {
  TopicsGroup,
  MessageItemProps,
  TopicProps,
} from '@/components/ConversationWithTutor/MessageItem/MessageItem';
import {
  TutorChatMessage,
  TutorChatSender,
  TutorChatStyle,
  TutorChatLevel,
  EllReport,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import { GetQuizzesResponse } from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import UserUtils from '@/utils/user';
import TutorTopics, { QuizStatus } from '@/utils/tutorTopics';
import { LevelSettingItem, TutorType } from '@/types/user';
import { TutorInformation, LevelUnit, UnitsTopic } from '@/types/unit-data';
import Common from '@/utils/common';
import config from '@/config';

export enum PrivateTutorTypes {
  START_OVER = 'start_over',
  QUIZ = 'quiz',
}

const PrivateTutor = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatcher = useContext(EventDispatcherContext);
  const { user, levels } = useAppSelector(authSelector);
  const [welcomeMessage, setWelcomeMessage] = useState<TutorChatMessage | undefined>({
    status: 'writing',
    sender: TutorChatSender.TUTOR,
  });
  const [startMessages, setStartMessages] = useState<MessageItemProps[] | undefined>();
  const [topicsGroups, setTopicsGroups] = useState<TopicsGroup[] | undefined>();
  const chatRef = useRef<ChatRef | null>(null);
  const abortSignalRef = useRef<AbortSignal | null>(null);

  const selectTopicMenu = useCallback(
    (openText: string, repeat: MessageItemProps[]) => {
      if (abortSignalRef.current?.aborted) {
        return;
      }

      const isTeacher = UserUtils.isTeacher(user);

      const levelsData = (levels || [])
        .filter(level => level.id !== 10)
        .sort((levelA, levelB) => TutorTopics.sortAscending(levelA.order, levelB.order));

      const startMessages: MessageItemProps[] = [];

      const updateStartMessages = (startMessages: MessageItemProps[]) => {
        setStartMessages([...repeat, ...startMessages]);
      };

      const createMessageItemProps = (text: string) => {
        const message: MessageItemProps = {
          message: {
            status: '',
            text: text,
            speakText: text,
            dateTime: new Date(),
            sender: TutorChatSender.TUTOR,
          },
          voice: '',
          showSpeaker: true,
        };

        return message;
      };

      const selectTutorTopic = async (topic: TopicProps) => {
        if (abortSignalRef.current?.aborted) {
          return;
        }

        startMessages[startMessages.length - 1].topicsGroups = undefined;
        updateStartMessages(startMessages);

        if (chatRef.current) {
          chatRef.current.setTopic(topic);
        }
      };

      const startOver = async (
        levelId: number,
        unitId: number,
        topicId: number,
        activityId: number
      ) => {
        let topic: TopicProps | null = null;

        if (levelId && unitId && topicId && activityId) {
          topic = await TutorTopics.getTutorTopicsForSpecificEllActivity(
            user,
            levelId,
            unitId,
            topicId,
            activityId
          );
        } else if (levelId && unitId && topicId) {
          topic = await TutorTopics.getTutorTopicsForSpecificEllTopic(
            user,
            levelId,
            unitId,
            topicId
          );
        } else if (levelId && unitId) {
          topic = await TutorTopics.getTutorTopicsForSpecificEllUnit(user, levelId, unitId);
        }

        if (abortSignalRef.current?.aborted) {
          return;
        }

        setWelcomeMessage(undefined);
        if (chatRef.current && topic) {
          chatRef.current.setTopic(topic);
        }
      };

      const openQuiz = async (
        enhancementArea: string,
        enhancementAreaSimplified: string,
        userId: string,
        groupId: string,
        fromScore?: number,
        toScore?: number
      ) => {
        let currentQuiz: GetQuizzesResponse | null = null;

        setWelcomeMessage(undefined);
        setStartMessages([
          {
            message: {
              sender: TutorChatSender.TUTOR,
              dateTime: new Date(),
              text: enhancementAreaSimplified,
              speakText: enhancementAreaSimplified,
              disableAutoSpeaker: true,
            },
            showSpeaker: false,
            voice: '',
          },
          {
            message: {
              sender: TutorChatSender.TUTOR,
              dateTime: new Date(),
              status: 'writing',
            },
            showSpeaker: false,
            voice: '',
          },
        ]);

        const quizzesList = await EllReport.getQuizzes({
          enhancementArea: enhancementArea,
          userId: userId,
          groupId: groupId,
          fromScore: fromScore,
          toScore: toScore,
        });

        const quizzes = (quizzesList || []).sort((q1, q2) => {
          const sortKey = TutorTopics.sortAscending(q1.score, q2.score);

          if (sortKey !== 0) {
            return sortKey;
          }

          return TutorTopics.sortAscending(q1.createdDate, q2.createdDate);
        });

        if (abortSignalRef.current?.aborted) {
          return;
        }

        const getTopic = (text: string, tempData: string) => {
          const topic: TopicProps = {
            level: TutorChatLevel.A1,
            style: TutorChatStyle.ROLEPLAY,
            system_role: '',
            user_role: '',
            topic: '',
            text: text,
            tempData: tempData,
          };

          return topic;
        };

        const handleTopic = (topic: TopicProps) => {
          setStartMessages(messages => {
            const message: MessageItemProps = {
              message: {
                sender: TutorChatSender.USER,
                dateTime: new Date(),
                text: topic.text,
                disableAutoSpeaker: true,
              },
              showSpeaker: false,
              voice: '',
            };

            const newMessage = [...(messages || []), message];

            if (newMessage.length > 1) {
              newMessage[newMessage.length - 2].topicsGroups = undefined;
              newMessage[newMessage.length - 2].quisGroups = undefined;
            }

            return newMessage;
          });

          switch (topic.tempData) {
            case 'finish':
              navigate(-1);
              break;
            case 'next':
              showQuiz();
              break;
            case 'try_again':
              if (currentQuiz) {
                showQuizMessage(currentQuiz);
              }
              break;
            case 'on_quiz_complete':
              onQuizComplete(0, 0, false);
              break;
          }
        };

        const onQuizComplete = async (
          correctAnswers: number,
          numberOfQuestions: number,
          checkIfDone: boolean = true
        ) => {
          const newScore =
            checkIfDone && numberOfQuestions > 0
              ? Math.ceil((100 * correctAnswers) / numberOfQuestions)
              : 0;
          if (
            currentQuiz &&
            userId &&
            numberOfQuestions > 0 &&
            (newScore > currentQuiz.score || !currentQuiz.isQuizDone)
          ) {
            await EllReport.setQuizAsDone({
              chatId: currentQuiz.chatId,
              enhancementArea: enhancementArea,
              userId: userId,
              score: newScore,
            });
          }

          if (abortSignalRef.current?.aborted) {
            return;
          }

          if (checkIfDone && correctAnswers < numberOfQuestions) {
            const oldIsDone = currentQuiz ? currentQuiz.isQuizDone : false;
            const oldScore = currentQuiz ? currentQuiz.score : 0;

            if (currentQuiz) {
              currentQuiz.score = newScore;
              currentQuiz.isQuizDone = true;
            }

            setStartMessages(messages => {
              const text = `You got ${correctAnswers} out of ${numberOfQuestions} questions correct. Do you want to try again?`;

              const topicsGroups = [
                {
                  topics: [getTopic('Yes', 'try_again'), getTopic('No', 'on_quiz_complete')],
                },
              ];

              const message: MessageItemProps = {
                message: {
                  sender: TutorChatSender.TUTOR,
                  dateTime: new Date(),
                  text: text,
                  speakText: text,
                  disableAutoSpeaker: true,
                },
                showSpeaker: false,
                voice: '',
                handleTopic: handleTopic,
                topicsGroups: topicsGroups,
              };

              const newMessage = [...(messages || []), message];

              if (newMessage.length > 1) {
                newMessage[newMessage.length - 2].topicsGroups = undefined;
                newMessage[newMessage.length - 2].quisGroups = undefined;
              }

              return newMessage;
            });

            return;
          }

          if (quizzes.length === 0) {
            showNoQuizMessage();
            return;
          }

          setStartMessages(messages => {
            const text = checkIfDone
              ? `You have successfully completed the lesson! You have ${quizzes.length} more lessons on this topic. Would you like to practice now?`
              : `You have ${quizzes.length} more lessons on this topic. Would you like to practice now?`;

            const topicsGroups = [
              {
                topics: [getTopic('Practice now!', 'next'), getTopic('Back home', 'finish')],
              },
            ];

            const message: MessageItemProps = {
              message: {
                sender: TutorChatSender.TUTOR,
                dateTime: new Date(),
                text: text,
                speakText: text,
                disableAutoSpeaker: true,
              },
              showSpeaker: false,
              voice: '',
              handleTopic: handleTopic,
              topicsGroups: topicsGroups,
            };

            const newMessage = [...(messages || []), message];

            if (newMessage.length > 1) {
              newMessage[newMessage.length - 2].topicsGroups = undefined;
              newMessage[newMessage.length - 2].quisGroups = undefined;
            }

            return newMessage;
          });
        };

        const showNoQuizMessage = () => {
          setStartMessages(messages => {
            const text =
              'Well done! You have completed this task. You can return to the homepage and practice your other tasks.';

            const topicsGroups = [
              {
                topics: [getTopic('Back home', 'finish')],
              },
            ];

            const message: MessageItemProps = {
              message: {
                sender: TutorChatSender.TUTOR,
                dateTime: new Date(),
                text: text,
                speakText: text,
                disableAutoSpeaker: true,
              },
              showSpeaker: false,
              voice: '',
              handleTopic: handleTopic,
              topicsGroups: topicsGroups,
            };

            const newMessage = [...(messages || []), message];

            if (newMessage.length > 1) {
              newMessage[newMessage.length - 2].topicsGroups = undefined;
              newMessage[newMessage.length - 2].quisGroups = undefined;
            }

            return newMessage;
          });
        };

        const showQuizMessage = (quiz: GetQuizzesResponse) => {
          currentQuiz = quiz;

          setStartMessages(messages => {
            const text = quiz.lessonText;

            const message: MessageItemProps = {
              message: {
                sender: TutorChatSender.TUTOR,
                dateTime: new Date(),
                text: text,
                speakText: text,
                disableAutoSpeaker: true,
              },
              showSpeaker: false,
              voice: '',
              onQuizComplete: onQuizComplete,
              quisGroups: quiz.quiz.map(q => {
                let tempAnswers = [...q.answers];
                const answers = [];

                while (tempAnswers.length > 0) {
                  const randomIndex = Common.getRandomInt(0, tempAnswers.length - 1);
                  answers.push(tempAnswers[randomIndex]);
                  tempAnswers = [
                    ...tempAnswers.slice(0, randomIndex),
                    ...tempAnswers.slice(randomIndex + 1),
                  ];
                }

                return {
                  question: q.question,
                  answers: answers,
                  correctAnswer: q['correct answer'] || (q as any).correct_answer,
                };
              }),
            };

            const newMessage = [...(messages || []), message];

            if (newMessage.length > 1) {
              newMessage[newMessage.length - 2].topicsGroups = undefined;
              newMessage[newMessage.length - 2].quisGroups = undefined;
            }

            return newMessage;
          });
        };

        const showQuiz = () => {
          const quiz = quizzes.shift();

          if (quiz) {
            showQuizMessage(quiz);
          } else {
            showNoQuizMessage();
          }
        };

        setStartMessages(messages => {
          if (messages && messages?.length > 1) {
            return [messages[0]];
          }

          return messages;
        });
        showQuiz();
      };

      const initStudentData = async () => {
        const message: MessageItemProps = createMessageItemProps(
          repeat.length === 0
            ? `Hi ${user?.metadata?.firstName}, thank you for calling me. I’m an AI assistant here to help you improve your English. Please choose one of the following topics to talk about and we can begin!`
            : openText +
                ' \nPlease choose one of the following topics to talk about and we can begin!'
        );
        message.message.status = 'writing';
        message.handleTopic = selectTutorTopic;
        startMessages.push(message);
        updateStartMessages(startMessages);
        setWelcomeMessage(undefined);

        const topicsGroups: TopicsGroup[] | undefined =
          await TutorTopics.getTutorTopicsForAllTopicsInLastUsersUnit(
            user,
            levelsData,
            abortSignalRef.current || undefined
          );
        // await TutorTopics.getTutorTopicsForLastUsersActivity(user, levelsData, abortSignal);

        if (abortSignalRef.current?.aborted) {
          return;
        }

        if (topicsGroups && topicsGroups.length > 0) {
          message.topicsGroups = topicsGroups;
        }

        message.message.status = '';

        updateStartMessages(startMessages);
      };

      const initTeacherData = async () => {
        let selectedLevel: LevelSettingItem;
        let selectedUnit: LevelUnit;

        const setSelectTopicMessage = async (topic: TopicProps) => {
          if (abortSignalRef.current?.aborted) {
            return;
          }

          startMessages[startMessages.length - 1].topicsGroups = undefined;

          const selectedMessage: MessageItemProps = createMessageItemProps(topic.text);
          selectedMessage.message.sender = TutorChatSender.USER;
          selectedMessage.message.speakText = undefined;
          startMessages.push(selectedMessage);

          const message: MessageItemProps = createMessageItemProps(`Let’s choose a topic!`);
          message.handleTopic = selectTutorTopic;
          message.message.status = 'writing';
          startMessages.push(message);

          updateStartMessages(startMessages);

          selectedUnit = topic.tempData;
          const unitTopics = await TutorTopics.getUnitTopics(
            user,
            selectedLevel.id,
            selectedUnit.id
          );

          const topicsGroup: TopicsGroup = {
            topics: unitTopics.map(unitTopic => {
              const topic = unitTopic.tutorInformation
                ? TutorTopics.tutorInformationToTopic(
                    unitTopic.tutorInformation as TutorInformation,
                    selectedLevel.id,
                    selectedUnit.id,
                    unitTopic.id
                  )
                : TutorTopics.createDefaultTopic(
                    unitTopic.name,
                    selectedLevel.id,
                    selectedUnit.id,
                    unitTopic.id
                  );

              return topic;
            }),
          };

          message.topicsGroups = [topicsGroup];
          message.message.status = '';

          if (abortSignalRef.current?.aborted) {
            return;
          }

          updateStartMessages(startMessages);
        };

        const setSelectUnitMessage = async (topic: TopicProps) => {
          if (abortSignalRef.current?.aborted) {
            return;
          }

          startMessages[startMessages.length - 1].topicsGroups = undefined;

          const selectedMessage: MessageItemProps = createMessageItemProps(topic.text);
          selectedMessage.message.sender = TutorChatSender.USER;
          selectedMessage.message.speakText = undefined;
          startMessages.push(selectedMessage);

          const message: MessageItemProps = createMessageItemProps(`Let’s choose a unit!`);
          message.handleTopic = setSelectTopicMessage;
          message.message.status = 'writing';
          startMessages.push(message);

          updateStartMessages(startMessages);

          selectedLevel = topic.tempData;
          const levelUnits = await TutorTopics.getLevelUnits(user, selectedLevel.id);
          const topicsGroup: TopicsGroup = {
            topics: levelUnits.map(unit => {
              const topic: TopicProps = {
                style: TutorChatStyle.SMALLTALK,
                text: unit.name || '',
                topic: '',
                user_role: '',
                system_role: '',
                level: TutorChatLevel.A1,
                tempData: unit,
              };
              return topic;
            }),
          };
          message.topicsGroups = [topicsGroup];
          message.message.status = '';

          if (abortSignalRef.current?.aborted) {
            return;
          }

          updateStartMessages(startMessages);
        };

        const setSelectLevelsMessage = async () => {
          const message: MessageItemProps = createMessageItemProps(
            repeat.length === 0
              ? `Hi ${user?.metadata?.firstName}, thank you for calling me. I’m an AI assistant here to help you improve your English. Please choose a level!`
              : openText + ' \nPlease choose a level!'
          );
          message.message.status = 'writing';
          message.handleTopic = setSelectUnitMessage;
          startMessages.push(message);
          updateStartMessages(startMessages);
          setWelcomeMessage(undefined);

          const topicsGroup: TopicsGroup = {
            topics: levelsData.map(level => {
              const levelCEFR_name =
                {
                  1: ' (Pre-A1)',
                  2: ' (A1)',
                  3: ' (A2)',
                  4: ' (B1)',
                  5: ' (B2)',
                }[level.id] || '';
              const topic: TopicProps = {
                style: TutorChatStyle.SMALLTALK,
                text: (level.name || '') + levelCEFR_name,
                topic: '',
                user_role: '',
                system_role: '',
                level: TutorChatLevel.A1,
                tempData: level,
              };
              return topic;
            }),
          };
          message.topicsGroups = [topicsGroup];
          message.message.status = '';

          if (abortSignalRef.current?.aborted) {
            return;
          }

          updateStartMessages(startMessages);
        };

        setSelectLevelsMessage();
      };

      const type = state?.type || '';

      if (type === PrivateTutorTypes.START_OVER) {
        const { levelId, unitId, topicId, activityId } = state;
        startOver(levelId, unitId, topicId, activityId);
      } else if (type === PrivateTutorTypes.QUIZ) {
        const { enhancementArea, enhancementAreaSimplified, userId, groupId, fromScore, toScore } =
          state;
        openQuiz(enhancementArea, enhancementAreaSimplified, userId, groupId, fromScore, toScore);
      } else if (isTeacher) {
        initTeacherData();
      } else {
        initStudentData();
      }
    },
    [user, levels, state, navigate]
  );

  useEffect(() => {
    const abortController = new AbortController();
    const abortSignal = abortController.signal;
    abortSignalRef.current = abortSignal;

    selectTopicMenu('', []);

    return () => {
      abortController.abort();
    };
  }, [selectTopicMenu]);

  useEffect(() => {
    if (state?.type === PrivateTutorTypes.QUIZ || !config.useTutorStreaming) {
      return;
    }

    const handleOfflineClose = () => {
      chatRef.current?.stopAudio();
      navigate('/dashboard/my-tutor');
    };

    dispatcher.addListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, handleOfflineClose);

    return () => {
      dispatcher.removeListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, handleOfflineClose);
    };
  }, [navigate, dispatcher, state?.type]);

  const speechType: TutorType = useMemo(() => {
    if (
      state?.type === PrivateTutorTypes.QUIZ &&
      user?.metadata?.organizationDetails?.tutorData?.tutorType === 'AVATAR'
    ) {
      return 'SOUND';
    }

    return user?.metadata?.organizationDetails?.tutorData?.tutorType || 'TEXT';
  }, [user, state]);

  const onChatError = useCallback(
    (details: any) => {
      switch (details?.message || '') {
        case 'conversation init error: bad topic':
          const newMessages = [...(startMessages || [])];

          const selectedTopicMessage = chatRef.current?.getSelectedTopicMessages() || null;
          if (selectedTopicMessage) {
            newMessages.push({
              message: selectedTopicMessage,
              voice: '',
              showSpeaker: false,
            });
          }

          chatRef.current?.resetChat();
          selectTopicMenu(
            'I am sorry, but this topic is not approved for conversation.',
            newMessages
          );
          break;
        default:
          break;
      }
    },
    [selectTopicMenu, startMessages, user]
  );

  return (
    <div
      className={classes(classNames.tutorChat, {
        [classNames.tutorChatWithAvatar]: speechType === 'AVATAR',
        [classNames.tutorChatStudent]: !UserUtils.isTeacher(user),
      })}
      data-hide_site_footer={true}
    >
      <Chat
        welcomeMessage={welcomeMessage}
        startMessages={startMessages}
        onClose={() => {
          navigate(-1);
        }}
        speechType={speechType}
        topicsGroups={topicsGroups}
        ref={chatRef}
        layout='fullPage'
        endBtnText='Back home'
        onError={onChatError}
        hideTuorIcon={state?.type === PrivateTutorTypes.QUIZ}
        isFollowUpActivity={state?.type === PrivateTutorTypes.QUIZ}
      />
    </div>
  );
};

export default React.memo(PrivateTutor);
